import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { PopButton } from "../PopButton";
import { nextWeight } from "../../engine/wheels";
import type { WheelList } from "../../types";

export function WheelEditor({ initial, onSave, onCancel }: { initial: WheelList; onSave: (l: WheelList) => void; onCancel: () => void }) {
  const [name, setName] = useState(initial.name);
  const [options, setOptions] = useState<string[]>(initial.options.length ? initial.options : ["", ""]);
  const [weights, setWeights] = useState<number[]>(() => (initial.options.length ? initial.options : ["", ""]).map((o) => initial.weights?.[o] ?? 1));
  const clean = (o: string) => o.split(/\s+/).filter(Boolean).join(" ");
  const filled = options.map(clean).filter(Boolean);
  const weightMap = () => Object.fromEntries(options.map((o, i) => [clean(o), weights[i] ?? 1]).filter(([o, w]) => o && (w as number) > 1));
  const totalWeight = options.reduce((sum, o, i) => sum + (clean(o) ? weights[i] ?? 1 : 0), 0);
  const chance = (i: number) => (clean(options[i]) && totalWeight ? Math.round(((weights[i] ?? 1) / totalWeight) * 100) : 0);
  const dupes = filled.length !== new Set(filled.map((o) => o.toLowerCase())).size;
  const valid = name.trim() && filled.length >= 2 && !dupes;

  return (
    <div className="space-y-4 rounded-[22px] border-[3px] border-ink bg-white p-5 pop-shadow" data-testid="wheel-editor">
      <label className="block text-sm font-extrabold uppercase">Wheel name
        <input className="field mt-1" maxLength={60} placeholder="e.g. Takeaway spots" value={name} onChange={(e) => setName(e.target.value)} data-testid="wheel-name-input" />
      </label>
      <div>
        <p className="mb-2 text-sm font-extrabold uppercase">Options <span className="font-semibold normal-case text-zinc-500">({filled.length}/20) · tap 1× to favour an option (2× or 3× more likely)</span></p>
        <div className="space-y-2">
          {options.map((o, i) => (
            <div key={i} className="flex gap-2">
              <input className="field" maxLength={80} placeholder={`Option ${i + 1}`} value={o} data-testid={`wheel-option-input-${i}`}
                onChange={(e) => setOptions(options.map((x, j) => (j === i ? e.target.value : x)))} />
              <button type="button" onClick={() => setWeights(options.map((_, j) => (j === i ? nextWeight(weights[j] ?? 1) : weights[j] ?? 1)))}
                aria-label={`Weight ${weights[i] ?? 1}x, tap to change`} title={`${chance(i)}% chance`} data-testid={`wheel-option-weight-${i}`} data-weight={weights[i] ?? 1}
                className={`w-16 shrink-0 rounded-xl border-2 border-ink font-display text-sm font-bold transition-colors ${(weights[i] ?? 1) === 3 ? "bg-pop-pink text-white" : (weights[i] ?? 1) === 2 ? "bg-pop-yellow" : "bg-white"}`}>
                {weights[i] ?? 1}×<span className="block text-[10px] font-extrabold opacity-70">{chance(i)}%</span>
              </button>
              {options.length > 2 && (
                <button aria-label="Remove option" className="rounded-xl border-2 border-ink px-3 hover:bg-pop-pink hover:text-white" onClick={() => { setOptions(options.filter((_, j) => j !== i)); setWeights(weights.filter((_, j) => j !== i)); }} data-testid={`wheel-option-remove-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        {dupes && <p className="mt-2 text-sm font-bold text-[#E11D48]" data-testid="wheel-duplicate-warning">Two options are the same.</p>}
        {options.length < 20 && (
          <button className="mt-3 inline-flex items-center gap-1 text-sm font-extrabold uppercase hover:underline" onClick={() => { setOptions([...options, ""]); setWeights([...weights, 1]); }} data-testid="wheel-add-option-button">
            <Plus className="h-4 w-4" strokeWidth={3} /> Add option
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <PopButton tone="orange" disabled={!valid} onClick={() => onSave({ ...initial, name: name.trim(), options: filled, weights: weightMap() })} data-testid="wheel-save-button">Save wheel</PopButton>
        <PopButton tone="white" onClick={onCancel} data-testid="wheel-cancel-button">Cancel</PopButton>
      </div>
    </div>
  );
}
